// pwa-register.js — Phase 6 service worker registration.
//
// Thin wrapper around vite-plugin-pwa's `virtual:pwa-register` module.
// We keep this in its own file (rather than calling registerSW() directly
// from main.js) for two reasons:
//
//   1. The `virtual:pwa-register` module is a virtual module injected by
//      vite-plugin-pwa at build time. Importing it from test code under
//      vitest would fail because the plugin isn't active during the test
//      run. Keeping it behind a named function means tests can import
//      this module safely by mocking this export — and main.js itself
//      is already excluded from coverage and test imports.
//
//   2. Error handling and logging conventions live in one place. If a
//      browser refuses to register the SW (private mode, missing HTTPS,
//      unsupported API) we want a single, quiet warning — not a UI
//      prompt. This is a children's app: SW failures must never block
//      the grid from rendering.
//
// Update strategy: configured as `registerType: 'autoUpdate'` in
// vite.config.js. That means when a new SW is detected, it will take
// over silently on the next cold load. We don't show an update prompt
// in-app (no UI surface for a toddler to fat-finger), and we don't
// skip-waiting mid-session (that can cause mismatches between running
// code and freshly-cached assets). Simple and quiet by design.

/**
 * Register the service worker. Safe to call from a non-browser
 * environment — it no-ops if the serviceWorker API is missing, and it
 * swallows any registration errors so a broken SW can never block the
 * app from booting.
 *
 * @returns {Promise<void>} Resolves when registration completes (or is
 *   skipped). Callers generally don't need to await this.
 */
export async function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  try {
    // Dynamic import so the virtual module is never resolved under
    // vitest (which runs without vite-plugin-pwa in its plugin list).
    // Under production and dev the import resolves normally because
    // the plugin is active in the main vite.config.js.
    const mod = await import('virtual:pwa-register');
    if (typeof mod.registerSW === 'function') {
      mod.registerSW({
        immediate: true,
        // autoUpdate handles onNeedRefresh/onOfflineReady itself, but
        // we still provide onRegisterError so unusual environments
        // (self-signed certs on a LAN, private browsing quirks) log
        // a single quiet warning instead of silently breaking.
        onRegisterError(err) {
          console.warn('[pwa-register] SW registration failed:', err);
        },
      });
    }
  } catch (err) {
    // Virtual module wasn't resolvable (e.g. under vitest, or a very
    // old Vite version). Swallow — SW is a progressive enhancement.
    console.warn('[pwa-register] virtual:pwa-register not available:', err?.message);
  }
}
